import { useCallback } from 'react';

import { useShowConsumerCommunicationStatus } from '@givebacks/api/backer';
import { Box, LoadingOverlay, Modal, Stack, Text, TransitionElement } from 'ui';
import { CauseFollowCard } from '~/pages/Communications/components/CauseFollowCard';

import {
  ModalTitleWithBackButton,
  useCommunicationListsModalProvider,
} from './CommunicationListsProvider';
import { OrgInfoContent } from './OrgInfoContent/OrgInfoContent';

const CauseNotAvailable = () => {
  return (
    <Stack gap={4}>
      <Text fz="lg" fw={600}>
        Unable to load this organization’s details.
      </Text>
      <Text fz="sm" c="gray">
        If the issue continues, reach out to support.
      </Text>
    </Stack>
  );
};

const CauseStatusModalContent = () => {
  const { meta, setMeta, openModalById } = useCommunicationListsModalProvider({ id: 'CauseStatus' });
  const { isLoading, isError } = useShowConsumerCommunicationStatus({ causeId: meta?.causeId });

  const handleOpenUnfollowModal = useCallback(() => {
    setMeta({ causeId: meta?.causeId });
    openModalById('Unfollow');
  }, [setMeta, meta?.causeId, openModalById]);

  return (
    <>
      {isLoading && (
        <Box h="120">
          <LoadingOverlay visible />
        </Box>
      )}
      <TransitionElement mounted={!isLoading}>
        {isError ? (
          <CauseNotAvailable />
        ) : (
          <>
            <Stack>
              <CauseFollowCard
                causeId={meta?.causeId}
                fullWidth
                onOpenUnfollowModal={handleOpenUnfollowModal}
              />
              <OrgInfoContent causeId={meta?.causeId ?? ''} />
            </Stack>
          </>
        )}
      </TransitionElement>
    </>
  );
};

const CauseStatusModal = ({ title }: { title?: string }) => {
  const { opened, close, props } = useCommunicationListsModalProvider({ id: 'CauseStatus' });
  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<ModalTitleWithBackButton>{title ?? 'Lists'}</ModalTitleWithBackButton>}
      {...props}
    >
      <CauseStatusModalContent />
    </Modal>
  );
};

export { CauseStatusModal };
